import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  ordersCreateFulfillmentOrderShipmentMutation,
  type ShipFulfillmentOrderResponse,
  type ShippingOptionRoot,
  type CarrierRoot,
} from '@repo/sdk'

import { extractUserFacingMessage } from '../../providers/sdkErrorUtils'

import { useInvalidateFulfillmentOrders } from './useInvalidateFulfillmentOrders'

interface ShipOrderParams {
  fulfillmentOrderId: string
  shippingOption: ShippingOptionRoot
  carrier?: CarrierRoot | null
  trackingNumber?: string | null
}

interface UseShipOrderOptions {
  /** Called as soon as the shipment is persisted, before the views are refreshed. */
  onShipped?: (result: ShipFulfillmentOrderResponse) => void
}

export function useShipOrder(options: UseShipOrderOptions = {}) {
  const invalidateFulfillmentOrders = useInvalidateFulfillmentOrders()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutate, isPending } = useMutation({
    ...ordersCreateFulfillmentOrderShipmentMutation(),
    onSuccess: (data) => {
      message.success(t('common.modals.shipSuccess'))
      // Navigate first: invalidating the buyer new-order group refetches it, the shipped
      // buyer is gone from it, and the still-mounted group detail would flash "not found".
      options.onShipped?.(data)
      void invalidateFulfillmentOrders(data.fulfillmentOrderId)
    },
    onError: (error) => {
      // The server explains why a combined shipment was refused (409: broadcast not
      // ended, differing shipping addresses…) — showing a generic message hid that.
      message.error(extractUserFacingMessage(error) ?? t('common.modals.shipFailed'))
    },
  })

  const shipOrder = (
    { fulfillmentOrderId, shippingOption, carrier, trackingNumber }: ShipOrderParams,
    callbacks?: { onSuccess?: () => void },
  ) => {
    mutate(
      {
        path: { fulfillmentOrderId },
        body: { shippingOption, carrier, trackingNumber },
      },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    )
  }

  return { shipOrder, isShipping: isPending }
}
